import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,
  faSpinner,
  faCalendarAlt,
  faBuilding,
  faTrash,
  faFileAlt,
  faUsers,
  faCommentDots,
  faSave,
} from "@fortawesome/free-solid-svg-icons";
import { getApiUrl, SERVERS, ENDPOINTS } from "../config/api";
import CSVUpload from "../components/CSVUpload";
import "./DataOperatorUpload.css";

const DataOperatorUpload = () => {
  // Form metadata state
  const [formData, setFormData] = useState({
    academicYear: "",
    semester: "",
    groupDepartmentName: "",
    uploadedBy: "", // Default value, can be made dynamic based on auth
  });
  const [authLoading, setAuthLoading] = useState(true);
  const [dataerror, setDataerror] = useState(null);

  const [userData, setUserData] = useState({});
  // CSV files state
  const [professorsFile, setProfessorsFile] = useState(null);
  const [coursesFile, setCoursesFile] = useState(null);
  const [feedbackFile, setFeedbackFile] = useState(null);

  // CSV preview state
  const [professorsPreview, setProfessorsPreview] = useState(null);
  const [coursesPreview, setCoursesPreview] = useState(null);
  const [feedbackPreview, setFeedbackPreview] = useState(null);

  // Templates and UI state
  const [templates, setTemplates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);


useEffect(() => {
  (async () => {
    try {
      const res = await fetch(`/qpra/get-saml-data`, {
        method: "GET",
        credentials: "include",
        headers: { Accept: "application/json" },
      });

      const data = await res.json();
      setUserData(data || {});

      // ✅ Update formData.uploadedBy safely
      setFormData((prev) => ({
        ...prev,
        uploadedBy: data?.name || "",
      }));

      setError(null);
    } catch (err) {
      console.error("Error fetching SAML data:", err);
      setUserData({});
      setError(err.message || "Unknown error");
    } finally {
      setAuthLoading(false);
    }
  })();
}, []);



  // Academic years dropdown
  const academicYears = [];
  const currentYear = new Date().getFullYear();
  for (let i = 0; i < 10; i++) {
    const year = currentYear - i;
    academicYears.push(`${year}-${(year + 1).toString().slice(-2)}`);
  }

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const templateUrl = getApiUrl(
        SERVERS.REVIEWER,
        ENDPOINTS.SIMILARITY_CSV_TEMPLATES
      );
      const response = await fetch(templateUrl);
      if (response.ok) {
        const templatesData = await response.json();
        setTemplates(templatesData);
      }
    } catch (e) {
      console.error("Failed to load templates:", e);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
    setSuccess(null);
  };

  const parseCSVForPreview = (csvText) => {
    const lines = csvText.split("\n").filter((line) => line.trim());
    if (lines.length === 0) return { headers: [], rows: [], totalRows: 0 };
    const headers = lines[0].split(",").map((h) => h.trim().replace(/"/g, ""));
    const rows = lines
      .slice(1, 6)
      .map((line) =>
        line.split(",").map((cell) => cell.trim().replace(/"/g, ""))
      );
    return { headers, rows, totalRows: lines.length - 1 };
  };

  const handleProfessorsFileUpload = async (file) => {
    setProfessorsFile(file);
    setError(null);
    setSuccess(null);
    try {
      setProfessorsPreview(parseCSVForPreview(await file.text()));
    } catch {
      setError("Failed to read professors CSV file");
      setProfessorsPreview(null);
    }
  };

  const handleCoursesFileUpload = async (file) => {
    setCoursesFile(file);
    setError(null);
    setSuccess(null);
    try {
      setCoursesPreview(parseCSVForPreview(await file.text()));
    } catch {
      setError("Failed to read courses CSV file");
      setCoursesPreview(null);
    }
  };

  const handleFeedbackFileUpload = async (file) => {
    setFeedbackFile(file);
    setError(null);
    setSuccess(null);
    try {
      setFeedbackPreview(parseCSVForPreview(await file.text()));
    } catch {
      setError("Failed to read feedback CSV file");
      setFeedbackPreview(null);
    }
  };

  const validateForm = () => {
    if (!formData.academicYear) {
      setError("Academic Year is required");
      return false;
    }
    if (!formData.semester) {
      setError("Semester is required");
      return false;
    }
    if (!formData.groupDepartmentName.trim()) {
      setError("Group/Department Name is required");
      return false;
    }
    if (!professorsFile || !coursesFile || !feedbackFile) {
      setError("All three CSV files are required");
      return false;
    }
    return true;
  };

  const handleUpload = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Read CSV file contents
      const professorsData = await professorsFile.text();
      const coursesData = await coursesFile.text();
      const feedbackData = await feedbackFile.text();

      const payload = {
        academic_year: formData.academicYear,
        semester: formData.semester,
        group_department_name: formData.groupDepartmentName.trim(),
        professors_csv_data: professorsData,
        courses_csv_data: coursesData,
        feedback_csv_data: feedbackData,
        uploaded_by: formData.uploadedBy,
      };

      // Upload to backend
      const response = await fetch(
        getApiUrl(SERVERS.DATABASE, "CSV_UPLOADS"), // We'll need to add this endpoint
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || `Upload failed with status ${response.status}`
        );
      }

      const result = await response.json();
      setSuccess(
        `CSV files uploaded successfully! ${
          result.overridden
            ? "Previous files for this combination were overridden."
            : ""
        }`
      );

      // Clear form after successful upload
      clearAll();
    } catch (e) {
      console.error("Upload error:", e);
      setError(e.message || "Failed to upload CSV files");
    } finally {
      setLoading(false);
    }
  };

  const clearAll = () => {
    setProfessorsFile(null);
    setCoursesFile(null);
    setFeedbackFile(null);
    setProfessorsPreview(null);
    setCoursesPreview(null);
    setFeedbackPreview(null);
    setError(null);
    setSuccess(null);
    // Don't clear form metadata as user might want to upload more files for same combination
  };

  const canUpload =
    formData.academicYear &&
    formData.semester &&
    formData.groupDepartmentName.trim() &&
    professorsFile &&
    coursesFile &&
    feedbackFile &&
    !loading;

  return (
    <div className="data-operator-upload-page">
      {/* Header Section */}
      <div className="page-header">
        <div className="header-content">
          <h1>
            <FontAwesomeIcon icon={faUpload} className="page-icon" style={{ marginRight: "6px" }}/>
            CSV Data Upload
          </h1>
          <p className="page-description">
            Upload CSV files with metadata for reviewer assignment generation.
            Data Operator role functionality.
          </p>
        </div>
      </div>

      {/* Metadata Form Section */}
      <div className="metadata-section">
        <div className="section-header">
          <h2>
            <FontAwesomeIcon icon={faInfoCircle} className="section-icon" style={{ marginRight: "6px" }}/>
            Upload Metadata
          </h2>
          <p>Provide the context information for your CSV uploads</p>
        </div>

        <div className="metadata-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="academicYear">
                <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: "6px" }}/>
                Academic Year *
              </label>
              <select
                id="academicYear"
                name="academicYear"
                value={formData.academicYear}
                onChange={handleInputChange}
                className="form-control"
                required
              >
                <option value="">Select Academic Year</option>
                {academicYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="semester">
                <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: "6px" }}/>
                Semester *
              </label>
              <select
                id="semester"
                name="semester"
                value={formData.semester}
                onChange={handleInputChange}
                className="form-control"
                required
              >
                <option value="">Select Semester</option>
                <option value="1">Semester 1</option>
                <option value="2">Semester 2</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="groupDepartmentName">
                <FontAwesomeIcon icon={faBuilding} style={{ marginRight: "6px" }}/>
                Group/Department Name *
              </label>
              <select
                id="groupDepartmentName"
                name="groupDepartmentName"
                value={formData.groupDepartmentName}
                onChange={handleInputChange}
                className="form-control"
                required
              >
                <option value="">Select Group/Department</option>
                <option value="Core Eng">Core Eng</option>
                <option value="CSIS">CSIS</option>
                <option value="Management">Management</option>
                <option value="EEE">EEE</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="uploadedBy">
                <FontAwesomeIcon icon={faUsers} style={{ marginRight: "6px" }}/>
                Uploaded By
              </label>
              <input
                type="text"
                id="uploadedBy"
                name="uploadedBy"
                value={formData.uploadedBy}
                className="form-control"
                placeholder="Data Operator Name"
                readOnly
              />
            </div>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="upload-section">
        <div className="section-header">
          <h2>
            <FontAwesomeIcon icon={faUpload} className="section-icon" style={{ marginRight: "6px" }}/>
            CSV File Uploads
          </h2>
          <button
            className="clear-all-btn"
            onClick={clearAll}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faTrash} style={{ marginRight: "6px" }}/>
            Clear Files
          </button>
        </div>

        <div className="upload-grid">
          <CSVUpload
            title="Professors Data"
            onFileUpload={handleProfessorsFileUpload}
            templateData={templates?.professors_template}
            uploadedFile={professorsFile}
            previewData={professorsPreview}
            isLoading={loading}
            error={
              professorsFile && !professorsPreview ? "Invalid CSV format" : null
            }
          />
          <CSVUpload
            title="Courses Data"
            onFileUpload={handleCoursesFileUpload}
            templateData={templates?.courses_template}
            uploadedFile={coursesFile}
            previewData={coursesPreview}
            isLoading={loading}
            error={coursesFile && !coursesPreview ? "Invalid CSV format" : null}
          />
          <CSVUpload
            title="Feedback Review Data"
            onFileUpload={handleFeedbackFileUpload}
            templateData={templates?.feedback_template}
            uploadedFile={feedbackFile}
            previewData={feedbackPreview}
            isLoading={loading}
            error={
              feedbackFile && !feedbackPreview ? "Invalid CSV format" : null
            }
          />
        </div>

        {/* CSV Format Information */}
        {templates && (
          <div className="format-info">
            <h3>
              <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: "6px" }}/>
              CSV Format Requirements
            </h3>
            <div className="format-grid">
              <div className="format-section">
                <h4>Professors CSV Columns:</h4>
                <ul>
                  {(templates.professors_required_columns || []).map((col) => (
                    <li key={col}>
                      <code>{col}</code>
                      {templates.notes?.[col] && (
                        <span> - {templates.notes[col]}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="format-section">
                <h4>Courses CSV Columns:</h4>
                <ul>
                  {(templates.courses_required_columns || []).map((col) => (
                    <li key={col}>
                      <code>{col}</code>
                      {templates.notes?.[col] && (
                        <span> - {templates.notes[col]}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="format-section">
                <h4>Feedback CSV Columns:</h4>
                <ul>
                  {(templates.feedback_required_columns || []).map((col) => (
                    <li key={col}>
                      <code>{col}</code>
                      {templates.notes?.[col] && (
                        <span> - {templates.notes[col]}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Upload Action Section */}
      <div className="upload-action-section">
        <div className="upload-controls">
          <button
            className={`upload-btn ${canUpload ? "ready" : "disabled"}`}
            onClick={handleUpload}
            disabled={!canUpload}
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="spinning" style={{ marginRight: "6px" }}/>
                Uploading CSV Files...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} style={{ marginRight: "6px" }}/>
                Upload CSV Files
              </>
            )}
          </button>

          {professorsPreview && coursesPreview && feedbackPreview && (
            <div className="upload-info">
              <div className="info-item">
                <FontAwesomeIcon icon={faUsers} style={{ marginRight: "6px" }}/>
                <span>{professorsPreview.totalRows} professors</span>
              </div>
              <div className="info-item">
                <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: "6px" }}/>
                <span>{coursesPreview.totalRows} courses</span>
              </div>
              <div className="info-item">
                <FontAwesomeIcon icon={faCommentDots} style={{ marginRight: "6px" }}/>
                <span>{feedbackPreview.totalRows} feedback rows</span>
              </div>
            </div>
          )}
        </div>

        {/* Status Messages */}
        {error && (
          <div className="error-message">
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: "6px" }}/>
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="success-message">
            <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: "6px" }}/>
            <span>{success}</span>
          </div>
        )}
      </div>

      {/* Information Section */}
      <div className="info-section">
        <div className="info-card">
          <h3>
            <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: "6px" }}/>
            Important Notes
          </h3>
          <ul>
            <li>
              <strong>Available Groups/Departments:</strong> Core Eng, CSIS, Management, EEE
            </li>
            <li>
              <strong>CSV File Requirements:</strong>
              <ul>
                <li><strong>Professors CSV:</strong> professor_id, professor_name, professor_email, dept</li>
                <li><strong>Courses CSV:</strong> course_id, course_name, dept</li>
                <li><strong>Feedback CSV:</strong> year_semester, faculty_name, faculty_id, faculty_email, course_id, course_name, total_participants</li>
              </ul>
            </li>
            <li>
              <strong>Unique Combination:</strong> Each combination of Academic
              Year, Semester, and Group/Department can have only one set of CSV
              files.
            </li>
            <li>
              <strong>Override Behavior:</strong> If you upload files for an
              existing combination, the previous files will be overridden.
            </li>
            <li>
              <strong>Important Note:</strong> Professor email IDs are required for SSO integration and course assignment lookup.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DataOperatorUpload;
